import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Plus, Maximize2, X, Share2, Twitter, Facebook, Link2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExhibitionPage() {
  const { id } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [allArtworks, setAllArtworks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { addItem } = useCart();

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const shareOnTwitter = () => {
    const text = `Check out "${artwork?.title}" by Akanni — Phaeton Imagery`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artworkRes, allRes, productsRes] = await Promise.all([
          axios.get(`${API}/api/artworks/${id}`),
          axios.get(`${API}/api/artworks`),
          axios.get(`${API}/api/products`)
        ]);
        setArtwork(artworkRes.data);
        setAllArtworks(allRes.data);
        setProducts(productsRes.data.filter(p => p.artwork_id === id));
      } catch (error) {
        console.error('Error fetching artwork:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-[#C5A059] font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#F5F5F0] mb-4">Artwork Not Found</h1>
          <Link to="/gallery" className="text-[#C5A059]">Back to Gallery</Link>
        </div>
      </div>
    );
  }

  // Find adjacent artworks for navigation
  const currentIdx = allArtworks.findIndex(a => a.id === id);
  const prevArtwork = currentIdx > 0 ? allArtworks[currentIdx - 1] : null;
  const nextArtwork = currentIdx < allArtworks.length - 1 ? allArtworks[currentIdx + 1] : null;
  const relatedArtworks = allArtworks.filter(a => a.id !== id).slice(0, 3);

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.title} added to cart`);
  };

  return (
    <div className="page-transition pt-28">
      {/* Fullscreen overlay */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center cursor-zoom-out"
          onClick={() => setFullscreen(false)}
        >
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-6 right-6 text-white/60 hover:text-white z-10"
            data-testid="close-fullscreen"
          >
            <X size={32} />
          </button>
          <img
            src={artwork.image_url}
            alt={artwork.title}
            className="max-w-[95vw] max-h-[95vh] object-contain"
          />
        </div>
      )}

      {/* Back navigation */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-8">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#C5A059] font-mono text-xs uppercase tracking-[0.2em] transition-colors"
          data-testid="back-to-gallery"
        >
          <ArrowLeft size={14} />
          Back to Exhibitions
        </Link>
      </div>

      {/* Main artwork display */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Image — large, full-screen capable */}
          <div className="lg:col-span-8">
            <div
              className="relative group cursor-zoom-in"
              onClick={() => setFullscreen(true)}
              data-testid="artwork-fullscreen-trigger"
            >
              <img
                src={artwork.image_url}
                alt={artwork.title}
                className="w-full h-auto max-h-[80vh] object-contain bg-[#121212]"
              />
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/60 backdrop-blur-sm p-2">
                  <Maximize2 size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Details sidebar */}
          <div className="lg:col-span-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3 block">
              {artwork.year}
            </span>
            <h1 className="font-serif text-4xl lg:text-5xl text-[#F5F5F0] tracking-tight leading-none mb-4">
              {artwork.title}
            </h1>

            {/* Share buttons */}
            <div className="flex items-center gap-2 mb-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] mr-2">
                Share
              </span>
              <button
                onClick={shareOnTwitter}
                className="w-8 h-8 flex items-center justify-center border border-white/10 text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                title="Share on Twitter"
                data-testid="share-twitter"
              >
                <Twitter size={14} strokeWidth={1.5} />
              </button>
              <button
                onClick={shareOnFacebook}
                className="w-8 h-8 flex items-center justify-center border border-white/10 text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                title="Share on Facebook"
                data-testid="share-facebook"
              >
                <Facebook size={14} strokeWidth={1.5} />
              </button>
              <button
                onClick={copyLink}
                className="w-8 h-8 flex items-center justify-center border border-white/10 text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                title="Copy link"
                data-testid="share-copy-link"
              >
                {copied ? <Check size={14} className="text-[#C5A059]" /> : <Link2 size={14} strokeWidth={1.5} />}
              </button>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed mb-8">
              {artwork.description}
            </p>

            {/* Details table */}
            <div className="space-y-4 border-t border-white/10 pt-6 mb-8">
              {artwork.medium && (
                <div className="flex justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">Medium</span>
                  <span className="text-[#F5F5F0] text-sm">{artwork.medium}</span>
                </div>
              )}
              {artwork.dimensions && (
                <div className="flex justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">Dimensions</span>
                  <span className="text-[#F5F5F0] text-sm">{artwork.dimensions}</span>
                </div>
              )}
              {artwork.year && (
                <div className="flex justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">Year</span>
                  <span className="text-[#F5F5F0] text-sm">{artwork.year}</span>
                </div>
              )}
            </div>

            {/* Purchase options */}
            {products.length > 0 && (
              <div className="space-y-3 mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block">
                  Available
                </span>
                {products.map(product => (
                  <button
                    key={product.id}
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.in_stock}
                    className="w-full flex items-center justify-between p-4 border border-white/10 hover:border-[#C5A059] transition-colors group disabled:opacity-40"
                    data-testid={`buy-${product.id}`}
                  >
                    <div className="text-left">
                      <p className="text-[#F5F5F0] text-sm">{product.product_type === 'original' ? 'Original' : 'Limited Print'}</p>
                      {product.edition_size && (
                        <p className="text-[#525252] text-xs font-mono">Edition of {product.edition_size}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#C5A059] font-serif text-xl">${product.price.toLocaleString()}</span>
                      <Plus size={16} className="text-[#525252] group-hover:text-[#C5A059] transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Artwork navigation */}
            <div className="flex gap-3 pt-6 border-t border-white/10">
              {prevArtwork ? (
                <Link
                  to={`/exhibition/${prevArtwork.id}`}
                  className="flex-1 flex items-center gap-2 p-3 border border-white/10 text-[#A3A3A3] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
                  data-testid="prev-artwork"
                >
                  <ArrowLeft size={14} />
                  <span className="text-xs font-mono uppercase tracking-wider truncate">Prev</span>
                </Link>
              ) : <div className="flex-1" />}
              {nextArtwork ? (
                <Link
                  to={`/exhibition/${nextArtwork.id}`}
                  className="flex-1 flex items-center justify-end gap-2 p-3 border border-white/10 text-[#A3A3A3] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
                  data-testid="next-artwork"
                >
                  <span className="text-xs font-mono uppercase tracking-wider truncate">Next</span>
                  <ArrowRight size={14} />
                </Link>
              ) : <div className="flex-1" />}
            </div>
          </div>
        </div>
      </section>

      {/* Related works */}
      {relatedArtworks.length > 0 && (
        <section className="py-16 bg-[#121212] border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <h2 className="font-serif text-2xl text-[#F5F5F0] mb-8">More Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArtworks.map(art => (
                <Link
                  key={art.id}
                  to={`/exhibition/${art.id}`}
                  className="group block"
                  data-testid={`related-${art.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={art.image_url}
                      alt={art.title}
                      className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-serif text-lg text-[#F5F5F0]">{art.title}</h3>
                      <p className="text-[#A3A3A3] text-xs font-mono mt-1">{art.year}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
